import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, ExternalLink, Check, X, Star, Archive, Trash2, Pencil, Download,
  Store, Inbox, RefreshCw, Flag, Sparkles, Settings as SettingsIcon, HandCoins,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { BusinessEditDialog } from "@/components/BusinessEditDialog";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, apiError, fileUrl } from "@/lib/api";

const STATUS_COLORS = {
  pending: "bg-gold/15 text-gold-dark", approved: "bg-emerald/15 text-emerald",
  rejected: "bg-ruby/10 text-ruby", archived: "bg-navy/10 text-navy",
};

function downloadCsv(path, filename) {
  api.get(path, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }).catch((e) => toast.error(apiError(e)));
}

function DirectoryTab() {
  const [data, setData] = useState({ businesses: [], counts: {} });
  const [filter, setFilter] = useState("pending");
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/businesses", { params: filter === "all" ? {} : { status: filter } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data.categories)); }, []);

  const act = async (id, updates) => {
    try { await api.patch(`/admin/businesses/${id}`, updates); toast.success("Updated."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently delete this listing?")) return;
    try { await api.delete(`/admin/businesses/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {["pending", "approved", "rejected", "archived", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`dir-filter-${s}`}
              className={`text-xs rounded-full px-4 py-1.5 border capitalize transition-colors ${filter === s ? "bg-navy text-ivory border-navy" : "bg-white text-navy/70 border-border"}`}>
              {s} {s !== "all" && data.counts?.[s] != null && `(${data.counts[s]})`}
            </button>
          ))}
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => downloadCsv("/admin/businesses/export", "businesses.csv")} data-testid="export-businesses">
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {data.businesses.length === 0 ? (
        <p className="text-muted-foreground py-10 text-center">No listings in this view.</p>
      ) : (
        <div className="grid gap-4">
          {data.businesses.map((b) => (
            <div key={b.id} className="rounded-xl border border-border bg-white p-5" data-testid={`admin-business-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-xl text-navy font-semibold">{b.name}</h3>
                    <Badge className={`border-none ${STATUS_COLORS[b.status]}`}>{b.status}</Badge>
                    {b.featured && <Badge className="bg-gold/15 text-gold-dark border-none">Featured</Badge>}
                    {b.is_demo && <Badge className="bg-navy/10 text-navy border-none">Demo</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{b.category} · {b.city}, {b.state} · from {b.submitter_name} ({b.submitter_email})</p>
                  <p className="text-sm text-muted-foreground mt-2 max-w-2xl line-clamp-2">{b.description}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {b.status !== "approved" && <Button size="sm" className="rounded-full bg-emerald hover:bg-emerald-dark text-white" onClick={() => act(b.id, { status: "approved" })} data-testid={`approve-${b.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>}
                {b.status !== "rejected" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(b.id, { status: "rejected" })} data-testid={`reject-${b.id}`}><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(b.id, { featured: !b.featured })}><Star className="mr-1 h-3.5 w-3.5" /> {b.featured ? "Unfeature" : "Feature"}</Button>
                {b.status !== "archived" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(b.id, { status: "archived" })}><Archive className="mr-1 h-3.5 w-3.5" /> Archive</Button>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(b)} data-testid={`edit-${b.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button size="sm" variant="ghost" className="rounded-full text-ruby" onClick={() => remove(b.id)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <BusinessEditDialog business={editing} categories={categories} open={!!editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={() => load()} />
    </div>
  );
}

function InquiriesTab() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const load = useCallback(() => {
    api.get("/admin/inquiries", { params: type === "all" ? {} : { type } })
      .then((r) => setItems(r.data.inquiries)).catch((e) => toast.error(apiError(e)));
  }, [type]);
  useEffect(() => { load(); }, [load]);
  const mark = async (id, status) => { try { await api.patch(`/admin/inquiries/${id}`, { status }); load(); } catch (e) { toast.error(apiError(e)); } };

  const TYPES = ["all", "church_property", "ministry_support", "artist_support", "general_contact"];
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-64 bg-white" data-testid="inquiry-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {TYPES.map((t) => <SelectItem key={t} value={t}>{t === "all" ? "All inquiries" : t.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-full" onClick={() => downloadCsv("/admin/inquiries/export", "inquiries.csv")} data-testid="export-inquiries"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
      </div>
      {items.length === 0 ? <p className="text-muted-foreground py-10 text-center">No inquiries yet.</p> : (
        <div className="grid gap-4">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-border bg-white p-5" data-testid={`inquiry-${it.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Badge className="bg-navy/10 text-navy border-none capitalize">{it.type.replace(/_/g, " ")}</Badge>
                  <Badge className={`border-none ${it.status === "new" ? "bg-emerald/15 text-emerald" : "bg-navy/10 text-navy"}`}>{it.status}</Badge>
                  <span className="text-xs text-muted-foreground">{new Date(it.created_at).toLocaleString()}</span>
                </div>
                <div className="flex gap-2">
                  {it.status !== "read" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => mark(it.id, "read")}>Mark read</Button>}
                  {it.status !== "archived" && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => mark(it.id, "archived")}>Archive</Button>}
                </div>
              </div>
              <dl className="mt-3 grid gap-1 text-sm sm:grid-cols-2">
                {Object.entries(it.data || {}).filter(([k]) => k !== "consent" && k !== "hp_field").map(([k, v]) => (
                  <div key={k} className="flex gap-2"><dt className="font-medium text-navy capitalize">{k.replace(/_/g, " ")}:</dt><dd className="text-muted-foreground break-words">{String(v)}</dd></div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SimpleListTab({ endpoint, emptyText, testid }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data.requests || r.data.reports || [])).catch(() => {}); }, [endpoint]);
  if (items.length === 0) return <p className="text-muted-foreground py-10 text-center">{emptyText}</p>;
  return (
    <div className="grid gap-4" data-testid={testid}>
      {items.map((it) => (
        <div key={it.id} className="rounded-xl border border-border bg-white p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg text-navy font-semibold">{it.business_name}</h3>
            <span className="text-xs text-muted-foreground">{new Date(it.created_at).toLocaleString()}</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{it.changes || it.reason}</p>
          <p className="mt-1 text-xs text-navy/60">{it.contact_name || it.reporter_email || "anonymous"} {it.email && `· ${it.email}`}</p>
        </div>
      ))}
    </div>
  );
}

function SpotlightsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: "ministry", title: "", summary: "", link: "", published: false });
  const load = () => api.get("/admin/spotlights").then((r) => setItems(r.data.spotlights)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title) return toast.error("Title is required.");
    try { await api.post("/admin/spotlights", form); toast.success("Spotlight created."); setForm({ kind: "ministry", title: "", summary: "", link: "", published: false }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const update = async (id, u) => { await api.patch(`/admin/spotlights/${id}`, u); load(); };
  const del = async (id) => { await api.delete(`/admin/spotlights/${id}`); load(); };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-border bg-white p-6">
        <h3 className="font-display text-xl text-navy font-semibold mb-4">New spotlight</h3>
        <div className="grid gap-3">
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="ministry">Ministry</SelectItem><SelectItem value="artist">Artist</SelectItem></SelectContent>
          </Select>
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white" data-testid="spotlight-title" />
          <Textarea placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="bg-white" />
          <Input placeholder="Link (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="bg-white" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /> Publish immediately</label>
          <Button className="rounded-full bg-emerald hover:bg-emerald-dark text-white" onClick={create} data-testid="create-spotlight">Create spotlight</Button>
          <p className="text-xs text-muted-foreground">Only publish real ministries or artists. Do not publish fictional examples as real recipients.</p>
        </div>
      </div>
      <div className="grid gap-3">
        {items.length === 0 ? <p className="text-muted-foreground py-6">No spotlights yet.</p> : items.map((s) => (
          <div key={s.id} className="rounded-xl border border-border bg-white p-5">
            <div className="flex items-center justify-between gap-2">
              <div>
                <Badge className="bg-navy/10 text-navy border-none capitalize mb-1">{s.kind}</Badge>
                <h4 className="font-display text-lg text-navy">{s.title}</h4>
              </div>
              <Badge className={`border-none ${s.published ? "bg-emerald/15 text-emerald" : "bg-gold/15 text-gold-dark"}`}>{s.published ? "Published" : "Draft"}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{s.summary}</p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => update(s.id, { published: !s.published })}>{s.published ? "Unpublish" : "Publish"}</Button>
              <Button size="sm" variant="ghost" className="rounded-full text-ruby" onClick={() => del(s.id)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GivingTab() {
  const [data, setData] = useState({ donations: [], total_cents: 0, paid_count: 0 });
  useEffect(() => { api.get("/admin/donations").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  return (
    <div data-testid="giving-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-4">
          <div className="rounded-xl border border-border bg-white px-5 py-3">
            <p className="text-xs text-muted-foreground">Total received (paid)</p>
            <p className="font-display text-2xl text-emerald font-semibold">${((data.total_cents || 0) / 100).toLocaleString()}</p>
          </div>
          <div className="rounded-xl border border-border bg-white px-5 py-3">
            <p className="text-xs text-muted-foreground">Completed gifts</p>
            <p className="font-display text-2xl text-navy font-semibold">{data.paid_count}</p>
          </div>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => downloadCsv("/admin/donations/export", "donations.csv")} data-testid="export-donations"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
      </div>
      {data.donations.length === 0 ? <p className="text-muted-foreground py-10 text-center">No gifts yet.</p> : (
        <div className="grid gap-3">
          {data.donations.map((d) => (
            <div key={d.session_id} className="rounded-xl border border-border bg-white p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-lg text-navy">{d.donor_name} <span className="text-muted-foreground text-sm">· {d.donor_email}</span></p>
                <p className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString()} {d.request_receipt && "· receipt requested"}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-navy/10 text-navy border-none capitalize">{d.frequency === "monthly" ? "Monthly" : "One-time"}</Badge>
                <span className="font-display text-xl text-navy font-semibold">${((d.amount || 0) / 100).toLocaleString()}</span>
                <Badge className={`border-none ${d.payment_status === "paid" ? "bg-emerald/15 text-emerald" : "bg-gold/15 text-gold-dark"}`}>{d.payment_status}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState({ contact_email: "", contact_phone: "", contact_address: "", donations_enabled: false });
  const imgRef = useRef();
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/admin/site-settings").then((r) => setS((prev) => ({ ...prev, ...r.data }))).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/site-settings", s); toast.success("Settings saved."); } catch (e) { toast.error(apiError(e)); } };
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const dollars = (c) => (c ? (Number(c) / 100).toString() : "");
  const onImpactImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("impact_image_url", data.url); toast.success("Image uploaded."); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2 max-w-5xl">
      <div className="rounded-xl border border-border bg-white p-6">
        <h3 className="font-display text-xl text-navy font-semibold mb-1">Public contact details</h3>
        <p className="text-xs text-muted-foreground mb-5">These are hidden on the site until you provide them.</p>
        <div className="grid gap-4">
          <div className="grid gap-1.5"><Label>Contact email</Label><Input value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="bg-white" data-testid="settings-email" /></div>
          <div className="grid gap-1.5"><Label>Contact phone</Label><Input value={s.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="bg-white" /></div>
          <div className="grid gap-1.5"><Label>Address</Label><Input value={s.contact_address || ""} onChange={(e) => set("contact_address", e.target.value)} className="bg-white" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.donations_enabled} onCheckedChange={(v) => set("donations_enabled", v)} /> Donations enabled (leave off until a payment account is approved)</label>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-6">
        <h3 className="font-display text-xl text-navy font-semibold mb-1">Giving goal (thermometer)</h3>
        <p className="text-xs text-muted-foreground mb-5">Shows a progress bar on the Fuel the Ministry page. Leave off to hide it.</p>
        <div className="grid gap-4">
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.giving_goal_enabled} onCheckedChange={(v) => set("giving_goal_enabled", v)} data-testid="goal-enabled" /> Show giving goal</label>
          <div className="grid gap-1.5"><Label>Goal name</Label><Input value={s.giving_goal_label || ""} onChange={(e) => set("giving_goal_label", e.target.value)} placeholder="e.g. Church Preservation Fund" className="bg-white" data-testid="goal-label" /></div>
          <div className="grid gap-1.5"><Label>Target amount (USD)</Label><Input type="number" value={dollars(s.giving_goal_target_cents)} onChange={(e) => set("giving_goal_target_cents", Math.round(parseFloat(e.target.value || 0) * 100))} placeholder="50000" className="bg-white" data-testid="goal-target" /></div>
          <div className="grid gap-1.5"><Label>Starting / offline amount already raised (USD, optional)</Label><Input type="number" value={dollars(s.giving_goal_base_cents)} onChange={(e) => set("giving_goal_base_cents", Math.round(parseFloat(e.target.value || 0) * 100))} placeholder="0" className="bg-white" /></div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-6 lg:col-span-2">
        <h3 className="font-display text-xl text-navy font-semibold mb-1">Impact story</h3>
        <p className="text-xs text-muted-foreground mb-5">Appears on the giving page only when you add text or an image. Share a real, true example — avoid unverified claims.</p>
        <div className="grid gap-4">
          <div className="grid gap-1.5"><Label>Heading</Label><Input value={s.impact_heading || ""} onChange={(e) => set("impact_heading", e.target.value)} placeholder="How your gifts are helping" className="bg-white" data-testid="impact-heading" /></div>
          <div className="grid gap-1.5"><Label>Story</Label><Textarea rows={4} value={s.impact_body || ""} onChange={(e) => set("impact_body", e.target.value)} className="bg-white" data-testid="impact-body" /></div>
          <div className="flex items-center gap-4">
            {s.impact_image_url ? <img src={fileUrl(s.impact_image_url)} alt="" className="h-16 w-24 rounded-lg object-cover" /> : <div className="h-16 w-24 rounded-lg bg-ivory-alt flex items-center justify-center text-xs text-muted-foreground">No image</div>}
            <input ref={imgRef} type="file" accept="image/*" hidden onChange={onImpactImg} />
            <Button type="button" variant="outline" className="rounded-full" disabled={uploading} onClick={() => imgRef.current?.click()}><Download className="mr-2 h-4 w-4 rotate-180" /> Upload image</Button>
            {s.impact_image_url && <Button type="button" variant="ghost" className="text-ruby" onClick={() => set("impact_image_url", "")}>Remove</Button>}
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <Button className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-8" onClick={save} data-testid="save-settings">Save all settings</Button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  usePageMeta("Admin Dashboard");
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-ivory">
      <header className="bg-navy text-ivory">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/lion-logo.webp" alt="" className="h-9 w-9 object-contain" />
            <span className="font-display text-xl font-semibold">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-ivory/70">{user?.email}</span>
            <Button asChild variant="outline" size="sm" className="rounded-full border-gold/40 text-ivory bg-transparent hover:bg-white/10"><Link to="/"><ExternalLink className="mr-1 h-3.5 w-3.5" /> View site</Link></Button>
            <Button size="sm" className="rounded-full bg-gold hover:bg-gold-dark text-navy" onClick={logout} data-testid="admin-logout"><LogOut className="mr-1 h-3.5 w-3.5" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <Tabs defaultValue="directory">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-ivory-alt">
            <TabsTrigger value="directory" data-testid="tab-directory" className="data-[state=active]:bg-emerald data-[state=active]:text-white"><Store className="mr-1.5 h-4 w-4" /> Directory</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="data-[state=active]:bg-emerald data-[state=active]:text-white"><Inbox className="mr-1.5 h-4 w-4" /> Inquiries</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="data-[state=active]:bg-emerald data-[state=active]:text-white"><RefreshCw className="mr-1.5 h-4 w-4" /> Update Requests</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="data-[state=active]:bg-emerald data-[state=active]:text-white"><Flag className="mr-1.5 h-4 w-4" /> Reports</TabsTrigger>
            <TabsTrigger value="spotlights" data-testid="tab-spotlights" className="data-[state=active]:bg-emerald data-[state=active]:text-white"><Sparkles className="mr-1.5 h-4 w-4" /> Spotlights</TabsTrigger>
            <TabsTrigger value="giving" data-testid="tab-giving" className="data-[state=active]:bg-emerald data-[state=active]:text-white"><HandCoins className="mr-1.5 h-4 w-4" /> Giving</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-emerald data-[state=active]:text-white"><SettingsIcon className="mr-1.5 h-4 w-4" /> Settings</TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="directory"><DirectoryTab /></TabsContent>
            <TabsContent value="inquiries"><InquiriesTab /></TabsContent>
            <TabsContent value="requests"><SimpleListTab endpoint="/admin/update-requests" emptyText="No update requests yet." testid="requests-list" /></TabsContent>
            <TabsContent value="reports"><SimpleListTab endpoint="/admin/reports" emptyText="No reports yet." testid="reports-list" /></TabsContent>
            <TabsContent value="spotlights"><SpotlightsTab /></TabsContent>
            <TabsContent value="giving"><GivingTab /></TabsContent>
            <TabsContent value="settings"><SettingsTab /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
